import { type RefObject, useEffect, useRef } from 'react';

type Options = {
  /** Element observed for intersection — usually a thin invisible div at the list end. */
  sentinelRef: RefObject<HTMLElement | null>;
  hasMore: boolean;
  isFetching: boolean;
  isError?: boolean;
  fetchNextPage: () => void;
  scrollContainerRef?: RefObject<HTMLElement | null>;
  rootMargin?: string;
};

export function useInfiniteScrollSentinel({
  sentinelRef,
  hasMore,
  isFetching,
  isError = false,
  fetchNextPage,
  scrollContainerRef,
  rootMargin = '200px 0px'
}: Options): void {
  const stateRef = useRef({ hasMore, isFetching, isError, fetchNextPage });
  stateRef.current = { hasMore, isFetching, isError, fetchNextPage };

  useEffect(() => {
    if (!hasMore) return undefined;
    const sentinel = sentinelRef.current;
    if (!sentinel) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;
        const { hasMore: hm, isFetching: f, isError: e, fetchNextPage: fn } = stateRef.current;
        if (hm && !f && !e) fn();
      },
      { root: scrollContainerRef?.current ?? null, rootMargin, threshold: 0 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, sentinelRef, scrollContainerRef, rootMargin]);
}
